'use client';

import { memo, useMemo } from 'react';
import { Box, VStack, HStack } from '@terra/ui-styled-system';
import {
  normalizeHttpHeaders,
  resolveEffectiveHttpVersion,
} from '@terra/shared-utils';
import { HttpEditor } from '../../http-editor';
import { ToolResultRenderer } from './ToolCallsRenderers/ToolResultRenderer';
import { prepareToolResultData } from './ToolCallsRenderers/content-detection';
import { LegacyDialog } from '../../../components/dialog';
import { IconButton } from '../../../components/styled/icon-button';
import { Text } from '../../../components/styled/text';
import { Cancel01Icon } from '@terra/ui-icons';

type HttpRequest = {
  method: string;
  url: string;
  headers: Record<string, string | string[]>;
  body?: string;
  httpVersion?: string;
};
type HttpResponse = {
  status?: number;
  statusMessage?: string;
  headers?: Record<string, string | string[]>;
  body?: string;
  httpVersion?: string;
};
type ToolCallHttpViewerDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  request: HttpRequest | null;
  response: HttpResponse | null;
  fallbackOutput?: unknown;
  toolName?: string;
  title?: string;
  renderMode?: 'dialog' | 'inline';
};

/**
 * Format HTTP request to string
 */
function formatHttpRequest(request: HttpRequest): string {
  const httpVer = resolveEffectiveHttpVersion({
    httpVersion: request.httpVersion,
    headers: request.headers,
  });
  let urlPath = request.url;

  try {
    const url = new URL(request.url);

    urlPath = `${url.pathname}${url.search}`;
  } catch {
    urlPath = request.url;
  }

  const lines: string[] = [];

  lines.push(`${request.method} ${urlPath} ${httpVer}`);
  for (const [key, value] of Object.entries(
    normalizeHttpHeaders(request.headers || {}),
  )) {
    const headerValue = Array.isArray(value) ? value.join(', ') : value;

    lines.push(`${key}: ${headerValue}`);
  }

  // Add body if present
  if (request.body) {
    lines.push('');
    lines.push(request.body);
  }

  return lines.join('\n');
}

/**
 * Format HTTP response to string
 */
function formatHttpResponse(response: HttpResponse): string {
  if (!response.status) return '';
  const httpVer = resolveEffectiveHttpVersion({
    httpVersion: response.httpVersion,
    headers: response.headers,
  });
  const statusMessage = response.statusMessage || '';
  const lines: string[] = [];

  lines.push(`${httpVer} ${response.status} ${statusMessage}`);
  for (const [key, value] of Object.entries(
    normalizeHttpHeaders(response.headers || {}),
  )) {
    const headerValue = Array.isArray(value) ? value.join(', ') : value;

    lines.push(`${key}: ${headerValue}`);
  }

  // Add body if present
  if (response.body) {
    lines.push('');
    lines.push(response.body);
  }

  return lines.join('\n');
}

export const ToolCallHttpViewerDialog = memo(function ToolCallHttpViewerDialog({
  isOpen,
  onOpenChange,
  request,
  response,
  fallbackOutput,
  toolName,
  title,
  renderMode = 'dialog',
}: ToolCallHttpViewerDialogProps) {
  const dialogTitle =
    title || (request ? 'Full HTTP Request/Response' : 'Full Output');
  const requestContent = useMemo(
    () => (request ? formatHttpRequest(request) : ''),
    [request],
  );
  const responseContent = useMemo(
    () => (response ? formatHttpResponse(response) : ''),
    [response],
  );
  const content = (
    <VStack gap={4} padding={6} height="100%">
      <HStack justify="space-between" alignItems="center" width="100%">
        <Text fontSize="lg" fontWeight="semibold">
          {dialogTitle}
        </Text>
        <IconButton
          variant="ghost"
          size="sm"
          aria-label="Close"
          onClick={() => onOpenChange(false)}
        >
          <Cancel01Icon size={16} />
        </IconButton>
      </HStack>
      <Box
        width="100%"
        flex="1"
        overflow="hidden"
        minH="400px"
        {...(renderMode === 'dialog' ? { maxH: 'calc(95vh - 120px)' } : {})}
      >
        {request ? (
          <HStack
            alignItems="stretch"
            h="full"
            gap="0"
            width="100%"
            minH="400px"
          >
            <Box flex="1" minH={0} minW={0}>
              <HttpEditor
                value={requestContent}
                title="Request"
                readOnly
                showViewModeToggle
                viewMode="pretty"
              />
            </Box>
            <Box w="1px" bg="border.subtle" flexShrink={0} />
            <Box flex="1" minH={0} minW={0}>
              <HttpEditor
                value={responseContent}
                title="Response"
                statusCode={response?.status}
                readOnly
                showViewModeToggle
                viewMode="pretty"
              />
            </Box>
          </HStack>
        ) : fallbackOutput ? (
          <ToolResultRenderer
            data={prepareToolResultData({
              content: fallbackOutput,
              toolName,
            })}
          />
        ) : null}
      </Box>
    </VStack>
  );

  if (renderMode === 'inline') {
    return content;
  }

  return (
    <LegacyDialog.Root
      open={isOpen}
      onOpenChange={({ open }) => onOpenChange(open)}
      lazyMount
    >
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content maxWidth="95vw" width="95vw" maxHeight="95vh">
          {content}
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
});
// Re-export types for consumers
export type { HttpRequest, HttpResponse };
